import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "456",
  "slug": "largest-peninsulas-in-the-world",
  "title": "The Largest Peninsulas in the World, Ranked",
  "excerpt": "From Arabia to Scandinavia, the planet’s biggest peninsulas are practically continents in their own right. Here is a ranked tour of the giants of nearly-surrounded land.",
  "date": "2026-06-16",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1763377220339-de687c3efad4?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhcmFiaWFuJTIwcGVuaW5zdWxhJTIwY29hc3RsaW5lfGVufDF8MHx8fDE3ODE1OTc2NjR8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "The largest peninsula on Earth is the Arabian Peninsula at roughly 3.2 million square kilometres. The Indian subcontinent, Indochina, and the Scandinavian and Labrador peninsulas follow as the other true giants.",
  "content": [
    {
      "type": "paragraph",
      "text": "A peninsula is a piece of land almost entirely surrounded by water but still joined to a larger landmass. The word comes from the Latin for \"almost an island,\" which captures the idea perfectly. Some peninsulas are small enough to walk across; others are so vast they hold entire civilizations, climates, and mountain ranges."
    },
    {
      "type": "paragraph",
      "text": "Ranking them is a little fuzzy, because where you draw the line at the neck changes the area. Still, a clear set of giants stands out. Here are the largest peninsulas on Earth, roughly from biggest down."
    },
    {
      "type": "heading",
      "text": "1. The Arabian Peninsula"
    },
    {
      "type": "paragraph",
      "text": "At roughly 3.2 million square kilometres, the Arabian Peninsula is comfortably the largest on the planet, big enough to be considered a subcontinent. Hemmed by the Red Sea, the Arabian Sea, and the Persian Gulf, it holds Saudi Arabia and its neighbours, vast sand seas like the Rub al Khali, and some of the most strategically vital coastlines in the world."
    },
    {
      "type": "heading",
      "text": "2. The Indian Subcontinent"
    },
    {
      "type": "paragraph",
      "text": "Often treated as a subcontinent in its own right, the Indian peninsula juts south into the Indian Ocean, bordered by the Arabian Sea and the Bay of Bengal. The triangular Deccan Plateau forms its core, and the Himalayas wall it off to the north. By land area it is one of the largest peninsular regions on Earth."
    },
    {
      "type": "heading",
      "text": "3. Indochina"
    },
    {
      "type": "paragraph",
      "text": "The Indochinese Peninsula in Southeast Asia carries Myanmar, Thailand, Laos, Cambodia, and Vietnam, with the long Malay Peninsula trailing south from it toward the equator. Threaded by great rivers like the Mekong, it is a sprawling, biodiverse expanse of land between the Indian and Pacific oceans."
    },
    {
      "type": "heading",
      "text": "The Other Giants"
    },
    {
      "type": "paragraph",
      "text": "Below the top three, a cluster of major peninsulas shapes the map of the northern hemisphere and beyond."
    },
    {
      "type": "list",
      "items": [
        "Labrador Peninsula — the huge, ancient shield of eastern Canada, shared by Quebec and Newfoundland and Labrador.",
        "Scandinavian Peninsula — home to Norway and Sweden, defined by fjord-cut coasts and the Scandinavian Mountains.",
        "Balkan Peninsula — the rugged southeastern corner of Europe, bordered by the Adriatic, Aegean, and Black seas.",
        "Iberian Peninsula — Spain and Portugal, separated from the rest of Europe by the Pyrenees.",
        "Anatolian Peninsula (Asia Minor) — the broad bridge of land that makes up most of Turkey.",
        "Kamchatka Peninsula — a remote, volcano-studded arm of far-eastern Russia."
      ]
    },
    {
      "type": "heading",
      "text": "Why Peninsulas Punch Above Their Weight"
    },
    {
      "type": "paragraph",
      "text": "Because they are surrounded by water on most sides, peninsulas tend to have long, intricate coastlines and outsized maritime importance. The sea moderates their climate, opens them to trade, and often shelters them from neighbours. Iberia, Italy, Anatolia, and the Balkans each became cradles of distinct cultures partly because the water around them set them apart."
    },
    {
      "type": "paragraph",
      "text": "Coastlines also mean opportunity: harbours, fisheries, and beaches that draw people and commerce. It is no accident that so many of history’s seafaring powers grew up on peninsulas."
    },
    {
      "type": "heading",
      "text": "Honourable Mentions"
    },
    {
      "type": "paragraph",
      "text": "Below the true giants sits a long roster of peninsulas that loom large in culture and history even if they rank lower by area. Each is instantly recognizable on the map."
    },
    {
      "type": "list",
      "items": [
        "The Italian Peninsula — the famous boot kicking into the Mediterranean, home to Rome and the spine of the Apennines.",
        "The Korean Peninsula — a densely populated arm of East Asia divided between two nations.",
        "Baja California — the long, slender dagger of land that trails south from northwestern Mexico.",
        "The Yucatán Peninsula — the limestone shelf separating the Gulf of Mexico from the Caribbean.",
        "Jutland — the mainland heart of Denmark, reaching up from continental Europe.",
        "The Kola and Crimean peninsulas — strategic arms of land that have shaped European history out of all proportion to their size."
      ]
    },
    {
      "type": "paragraph",
      "text": "Part of what makes peninsulas tricky to rank is deciding where the neck ends and the mainland begins. Move that line a few hundred kilometres and the area can change dramatically, which is why different sources sometimes disagree on the exact order."
    },
    {
      "type": "heading",
      "text": "Reading Peninsulas From Above"
    },
    {
      "type": "paragraph",
      "text": "Peninsulas are some of the easiest landforms to recognize from a satellite view, because their shape does the work for you. Look for a bold finger or wedge of land reaching out into the sea, connected to the mainland by a comparatively narrow base. Italy’s boot, the dagger of Baja California, and the blunt mass of Arabia are unmistakable from orbit."
    },
    {
      "type": "paragraph",
      "text": "That instant shape recognition is one of the most useful tools a location-guesser can build. Sharpen yours in EarthGuessr and see how quickly you can name a coastline from the air."
    }
  ]
};
